import { Edit3, MapPin, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import {
  ActiveStatusBadge,
  HealthStatusBadge,
} from "../../components/ui/dashboard/StatusCommon";
import InfoItem from "../../components/ui/Info";
import SkeletonForDetailsPage from "../../components/ui/SkeletonForDetailsPage";
import { buildApiUrl } from "../../lib/utils";
import DeleteVehicleModal from "./components/DeleteVehicleModal";
import VehicleAssignmentHistory from "./refactor/Vehicleassignmenthistory";
import FinanceTab from "./refactor/vehicleDetails/FinanceTab";
import FuelConsumptionTab from "./refactor/vehicleDetails/FuelConsumptionTab";
import MaintenanceHistoryTab from "./refactor/vehicleDetails/MaintenanceHistoryTab";
import TripHistoryTab from "./refactor/vehicleDetails/TripHistoryTab";
import { usePermissions } from "../../hooks/usePermissions";
import { useGetVehicleByIdQuery } from "./vehicleApiSlice";
import VehicleAssignmentModal from "./VehicleAssignmentModal";
import VehicleModal from "./VehicleModal";

const VehicleDetails = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState("finance");
  const [openModal, setOpenModal] = useState("");

  const { data, isLoading, isError, refetch } = useGetVehicleByIdQuery(
    id || "",
    {
      skip: !id,
    },
  );

  if (!id) {
    return <></>;
  }

  // Loading skeleton
  if (isLoading) {
    return <SkeletonForDetailsPage />;
  }

  if (isError || (!isLoading && !data?.data)) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">Error loading vehicle details</div>
      </div>
    );
  }

  const vehicle = data?.data;

  if (!vehicle && !isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">Vehicle not found</div>
      </div>
    );
  }

  const handleModal = () => {
    setOpenModal("");
    refetch();
  };

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        <div className="text-pColor text-[13px] flex flex-wrap items-center gap-x-4 gap-y-1 bg-white rounded-[10px] p-4">
          <button onClick={() => navigate("/admin/vehicles")}>
            All vehicles
          </button>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="24"
              viewBox="0 0 12 24"
            >
              <defs>
                <path
                  id="SVG1pzpbdYY"
                  fill="currentColor"
                  d="m7.588 12.43l-1.061 1.06L.748 7.713a.996.996 0 0 1 0-1.413L6.527.52l1.06 1.06l-5.424 5.425z"
                />
              </defs>
              <use
                fillRule="evenodd"
                href="#SVG1pzpbdYY"
                transform="rotate(-180 5.02 9.505)"
              />
            </svg>
          </div>
          <button onClick={() => navigate(`/admin/vehicles`)}>
            {vehicle?.vehicleName}
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-12 bg-white rounded-[10px] p-4 text-[16px]">
          <div className="flex items-center space-x-10">
            <img
              src={buildApiUrl(vehicle?.vehicleImageUrl)}
              alt={vehicle?.vehicleName}
              className="w-[8rem] h-[8rem] rounded-[10px] object-cover border border-gray-300 shadow-sm"
            />
          </div>

          <div className="w-full space-y-4">
            <div className="w-full text-sm">
              <div className="flex flex-col gap-6 lg:gap-20 lg:flex-row lg:items-center lg:justify-between">
                {/* Vehicle identity */}
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <h1
                      className="font-semibold text-base whitespace-nowrap overflow-hidden text-ellipsis max-w-[240px] sm:max-w-[320px] lg:max-w-[420px]"
                      title={vehicle.vehicleName}
                    >
                      {vehicle.vehicleName}
                    </h1>

                    <ActiveStatusBadge isActive={vehicle.isActive} />
                  </div>
                  <p className="text-pColor">{vehicle.vehicleModel}</p>
                </div>

                {/* Vehicle details */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-6 gap-y-4">
                  <InfoItem label="Vehicle type" value={vehicle.vehicleType} />
                  <InfoItem
                    label="Assigned driver"
                    value={vehicle.currentDriver}
                  />
                  <InfoItem
                    label="Chassis number"
                    value={vehicle.chassisNumber}
                  />
                  <InfoItem
                    label="License plate"
                    value={vehicle.licensePlate}
                  />

                  <div>
                    <p className="text-xs text-pColor mb-1">Vehicle status</p>
                    <ActiveStatusBadge isActive={vehicle.isActive} />
                  </div>

                  <div>
                    <p className="text-xs text-pColor mb-1">Health status</p>
                    <HealthStatusBadge healthStatus={vehicle.healthStatus} />
                  </div>

                  <div>
                    <p className="text-xs text-pColor mb-1">Rep status</p>
                    <span className="inline-block text-xs font-semibold border border-gray-300 px-2 py-0.5 rounded-full">
                      {vehicle.replacementStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="h-[2px] w-full bg-gray-200"></div>

            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex flex-wrap gap-2">
                {canCreate("VehicleAssignments") && (
                  <button
                    onClick={() => setOpenModal("add-driver")}
                    className="flex items-center px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/60 hover:text-white transition-colors h-[38px] text-sm"
                  >
                    Add driver
                  </button>
                )}
                <AppButton
                  onClick={() => {}}
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
                >
                  Track
                  <MapPin className="h-4 w-4 ml-2" />
                </AppButton>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {}}
                  className="flex items-center px-4 py-2 border border-pColor text-pColor rounded-lg h-[38px] text-sm"
                >
                  Change status
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    className="h-6 w-6 ml-2"
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="m7 10l5 5l5-5"
                    />
                  </svg>
                </button>
                {canUpdate("Vehicles") && (
                  <button
                    onClick={() => setOpenModal("edit")}
                    className="flex items-center px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/60 hover:text-white transition-colors h-[38px] text-sm"
                  >
                    Edit vehicle
                    <Edit3 className="h-4 w-4 ml-2" />
                  </button>
                )}
                {canDelete("Vehicles") && (
                  <button
                    onClick={() => setOpenModal("delete")}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-800 transition-colors h-[38px] text-sm"
                  >
                    Delete
                    <Trash2 className="h-4 w-4 ml-2" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-[10px] p-2 mt-5 h-[70px] flex items-center overflow-x-auto">
          <nav className="flex px-2 whitespace-nowrap min-w-max">
            <button
              onClick={() => setCurrentTab("finance")}
              className={`px-3 py-1 mr-4 font-medium text-[16px] transition-colors ${
                currentTab === "finance"
                  ? "bg-primary/20 text-primary rounded-[8px]"
                  : "border-transparent text-pColor hover:text-hColor"
              }`}
            >
              Finance
            </button>
            <button
              onClick={() => setCurrentTab("trip-history")}
              className={`px-3 py-1 mr-4 font-medium text-[16px] transition-colors ${
                currentTab === "trip-history"
                  ? "bg-primary/20 text-primary rounded-[8px]"
                  : "border-transparent text-pColor hover:text-hColor"
              }`}
            >
              Trip history
            </button>
            <button
              onClick={() => setCurrentTab("fuel-consumption")}
              className={`px-3 py-1 mr-4 font-medium text-[16px] transition-colors ${
                currentTab === "fuel-consumption"
                  ? "bg-primary/20 text-primary rounded-[8px]"
                  : "border-transparent text-pColor hover:text-hColor"
              }`}
            >
              Fuel consumption
            </button>
            <button
              onClick={() => setCurrentTab("maintenance-history")}
              className={`px-3 py-1 mr-4 font-medium text-[16px] transition-colors ${
                currentTab === "maintenance-history"
                  ? "bg-primary/20 text-primary rounded-[8px]"
                  : "border-transparent text-pColor hover:text-hColor"
              }`}
            >
              Maintenance history
            </button>
          </nav>
        </div>

        {currentTab === "finance" && (
          <FinanceTab vehicle={vehicle} id={id} idType="vehicle" />
        )}

        {currentTab === "trip-history" && (
          <TripHistoryTab vehicleId={id} vehicle={vehicle} />
        )}

        {currentTab === "fuel-consumption" && (
          <FuelConsumptionTab vehicleId={id} vehicle={vehicle} />
        )}

        {currentTab === "maintenance-history" && (
          <MaintenanceHistoryTab vehicleId={id} vehicle={vehicle} />
        )}

        {currentTab === "Vehicle assignment history" && (
          <VehicleAssignmentHistory id={id} idType="vehicleId" />
        )}
      </div>

      <VehicleModal
        isOpen={openModal === "edit"}
        onClose={handleModal}
        vehicle={vehicle}
      />

      <VehicleAssignmentModal
        isOpen={openModal === "add-driver"}
        onClose={handleModal}
        vehicleId={id}
      />

      <DeleteVehicleModal
        isOpen={openModal === "delete"}
        onClose={handleModal}
        vehicle={vehicle}
      />
    </div>
  );
};

export default VehicleDetails;
